import { Alert, Button, Form, Input, Modal, Select, Space, message } from 'antd'
import { useEffect, useMemo, useState } from 'react'

import { errorText, listContractTemplates, previewContract, sendContract } from '~/services/teacher-contract'
import { getAllTeacher } from '~/services/teacher'
import { ITeacher } from '~/types/teacher'
import { ISendTeacherContractReq, ITeacherContractTemplateList } from '~/types/teacher-contract'

import PdfPreviewModal, { PdfPreview } from './PdfPreviewModal'
import { LANGUAGE_LABEL } from './constants'

interface Props {
  open: boolean
  onClose: () => void
  onSent: () => void
}

/** 选老师、选模板，填模板里系统填不了的变量；可先预览 PDF 再发送 */
const SendContractModal = ({ open, onClose, onSent }: Props) => {
  const [form] = Form.useForm()
  const [teachers, setTeachers] = useState<ITeacher[]>([])
  const [templates, setTemplates] = useState<ITeacherContractTemplateList | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [sending, setSending] = useState(false)
  const [pdf, setPdf] = useState<PdfPreview | null>(null)
  const templateId: number | undefined = Form.useWatch('templateId', form)

  useEffect(() => {
    if (!open) return
    form.resetFields()
    getAllTeacher(1, 1000)
      .then((res) => setTeachers(res.data?.content || []))
      .catch(async (err) => message.error(await errorText(err, '老师列表加载失败')))
    listContractTemplates()
      .then((res) => setTemplates(res.data))
      .catch(async (err) => message.error(await errorText(err, '模板加载失败')))
  }, [open, form])

  const template = templates?.items.find((t) => t.id === templateId)
  /** 模板里出现、但系统不会自动填的变量：要管理员手填 */
  const customKeys = useMemo(
    () => (template?.placeholders || []).filter((p) => !templates?.builtInPlaceholders.includes(p)),
    [template, templates],
  )

  useEffect(() => {
    if (template) form.setFieldValue('title', template.title)
  }, [template, form])

  const payload = async (): Promise<ISendTeacherContractReq> => {
    const v = await form.validateFields()
    const variables: Record<string, string> = {}
    customKeys.forEach((k) => (variables[k] = String(v[`var_${k}`] ?? '')))
    return {
      teacherId: v.teacherId,
      templateId: v.templateId,
      title: v.title,
      feeSummary: v.feeSummary,
      variables,
    }
  }

  const onPreview = async () => {
    const req = await payload()
    setPreviewing(true)
    try {
      setPdf({ blob: (await previewContract(req)).data, title: `预览：${req.title}`, fileName: 'PREVIEW.pdf' })
    } catch (err) {
      message.error(await errorText(err, '预览失败'))
    } finally {
      setPreviewing(false)
    }
  }

  const onSend = async () => {
    const req = await payload()
    const teacher = teachers.find((t) => String(t.id) === String(req.teacherId))
    Modal.confirm({
      title: '确认发送？',
      content: `发给 ${teacher?.name ?? ''}（${teacher?.email ?? ''}）。发出后内容不能再改，只能作废后重发。`,
      onOk: async () => {
        setSending(true)
        try {
          const res = await sendContract(req)
          message.success(`已发送 ${res.data.contractNo}，老师会收到邮件通知`)
          onSent()
        } catch (err) {
          message.error(await errorText(err, '发送失败'))
        } finally {
          setSending(false)
        }
      },
    })
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="发送合同"
      width={640}
      destroyOnClose
      footer={
        <Space>
          <Button onClick={onClose}>取消</Button>
          <Button loading={previewing} onClick={onPreview}>
            预览 PDF
          </Button>
          <Button type="primary" loading={sending} onClick={onSend}>
            发送
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical">
        <Form.Item name="teacherId" label="老师" rules={[{ required: true, message: '请选择老师' }]}>
          <Select
            showSearch
            placeholder="搜索姓名或邮箱"
            optionFilterProp="label"
            options={teachers.map((t) => ({ value: t.id, label: `${t.name} · ${t.email}` }))}
          />
        </Form.Item>
        <Form.Item name="templateId" label="合同模板" rules={[{ required: true, message: '请选择模板' }]}>
          <Select
            placeholder={templates?.items.length === 0 ? '还没有模板，请先在「合同模板」里新建' : '选择模板'}
            options={templates?.items.map((t) => ({ value: t.id, label: `[${LANGUAGE_LABEL[t.language]}] ${t.name}` }))}
          />
        </Form.Item>
        <Form.Item name="title" label="合同标题" rules={[{ required: true, message: '请填写标题' }]}>
          <Input maxLength={255} />
        </Form.Item>
        <Form.Item
          name="feeSummary"
          label="报酬摘要"
          extra="一行写清报酬条件，例如 PHP 90 per completed 25-minute lesson。会原样印在合同的报酬条款里（{{fee}}），也用于按金额检索合同（电子账簿保存法要求）。"
          rules={[{ required: true, message: '请填写报酬摘要' }]}
        >
          <Input maxLength={128} />
        </Form.Item>
        {customKeys.length > 0 && (
          <Alert
            style={{ marginBottom: 16 }}
            type="info"
            message="下面这些是模板里的自定义变量，需要手填；老师姓名、邮箱、国家、日期等由系统自动填。"
          />
        )}
        {customKeys.map((k) => (
          <Form.Item key={k} name={`var_${k}`} label={`{{${k}}}`} rules={[{ required: true, message: `请填写 ${k}` }]}>
            <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} />
          </Form.Item>
        ))}
      </Form>
      <PdfPreviewModal preview={pdf} onClose={() => setPdf(null)} />
    </Modal>
  )
}

export default SendContractModal
